import { useEffect, useState } from "react";
import { X, PenLine, Brush, Pen, Sparkles, Zap, Lock, Wand2 } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { AccountOut, PenType, VIP_EXCLUSIVE_PENS } from "../types";
import { useDesign } from "../state/designStore";
import { openExternalLink } from "../lib/telegram";
import { ColorSwatchButton } from "./ColorSheet";

const SUBSCRIBE_CONTACT_LINK = "https://t.me/zg_8bot";

const PEN_OPTIONS: { type: PenType; label: string; icon: typeof Pen }[] = [
  { type: "regular", label: "قلم عادي", icon: Pen },
  { type: "marker", label: "قلم خط", icon: PenLine },
  { type: "brush", label: "فرشاة", icon: Brush },
  { type: "neon", label: "نيون", icon: Zap },
];

const COLOR_PALETTE = [
  "#FFFFFF", "#000000", "#FF3B30", "#FF9500", "#FFCC00",
  "#34C759", "#00C7BE", "#30B0C7", "#007AFF", "#5856D6", "#FF2D92",
];

interface AiDrawResponse {
  strokes: { id: string; points: [number, number][] }[];
}

export function DrawingToolPopover({ onClose }: { onClose: () => void }) {
  const { activePen, setActivePen, setOverlayClose, addAiDrawingStrokes } = useDesign();
  const [penType, setPenType] = useState<PenType>(activePen?.penType ?? "regular");
  const [color, setColor] = useState(activePen?.color ?? "#000000");
  const [isVip, setIsVip] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then((res) => setIsVip(res.membership.tier === "vip"))
      .catch(() => undefined);
  }, []);

  const applyAndClose = (nextPenType: PenType, nextColor: string) => {
    setActivePen({ penType: nextPenType, color: nextColor });
    onClose();
  };

  const pickPen = (type: PenType) => {
    if (VIP_EXCLUSIVE_PENS.includes(type) && !isVip) return;
    setPenType(type);
  };

  const pickColor = (c: string) => {
    setColor(c);
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim() || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const res = await api.post<AiDrawResponse>("/api/drawing/ai-generate", { prompt: aiPrompt.trim() });
      const strokePoints = res.strokes.map((s) => s.points);
      addAiDrawingStrokes(strokePoints, color);
      onClose();
    } catch (err) {
      setAiError(err instanceof ApiError ? err.message : "تعذر توليد الرسمة");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div className="glass-card w-full max-w-md m-3 mb-4 p-4 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="font-semibold">أداة الرسم</span>
          <button type="button" onClick={onClose} aria-label="إغلاق" className="w-8 h-8 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="block text-sm text-tg-hint mb-2">نوع القلم</label>
          <div className="grid grid-cols-3 gap-2">
            {PEN_OPTIONS.map(({ type, label, icon: Icon }) => {
              const locked = VIP_EXCLUSIVE_PENS.includes(type) && !isVip;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => pickPen(type)}
                  className={`relative flex flex-col items-center gap-1.5 py-3 rounded-xl glass-pill ${
                    penType === type ? "ring-2 ring-tg-button" : ""
                  } ${locked ? "opacity-50" : ""}`}
                >
                  {locked && (
                    <span className="absolute top-1 left-1 w-4 h-4 rounded-full bg-vinyl-sunburst flex items-center justify-center">
                      <Lock size={9} className="text-black" strokeWidth={3} />
                    </span>
                  )}
                  <Icon size={20} strokeWidth={2} />
                  <span className="text-xs">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm text-tg-hint mb-2">لون الرسم</label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => pickColor(c)}
                className={`w-8 h-8 rounded-full border-2 ${color === c ? "border-tg-button" : "border-white/20"}`}
                style={{ backgroundColor: c }}
              />
            ))}
            <ColorSwatchButton value={color} onChange={pickColor} title="لون الرسم" size={32} rainbow />
          </div>
        </div>

        <div className="border-t border-white/10 pt-4">
          <label className="flex items-center gap-1.5 text-sm font-semibold mb-2">
            <Wand2 size={15} strokeWidth={2.25} />
            ارسم بالذكاء الاصطناعي
            {!isVip && (
              <span className="w-4 h-4 rounded-full bg-vinyl-sunburst flex items-center justify-center shrink-0">
                <Lock size={9} className="text-black" strokeWidth={3} />
              </span>
            )}
          </label>
          {isVip ? (
            <>
              <p className="text-xs text-tg-hint mb-2">اكتب وش تبي يرسمه (مثلاً: كلب) - ياخذ 5-15 ثانية</p>
              <div className="flex items-center gap-2 mb-2 text-xs text-tg-hint">
                <span>راح يترسم بهذا اللون:</span>
                <span className="w-5 h-5 rounded-full border-2 border-white/30 shrink-0" style={{ backgroundColor: color }} />
                <span className="font-mono" dir="ltr">{color.toUpperCase()}</span>
              </div>
              {/* Stacked (input full-width, button full-width below it)
                  instead of side-by-side - a side-by-side row could run
                  out of horizontal room on a narrow phone screen once
                  the button's own label text ("جار الرسم...") is
                  accounted for, clipping the button. Stacking removes
                  that constraint entirely regardless of screen width. */}
              <input
                type="text"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="كلب"
                dir="rtl"
                disabled={aiBusy}
                className="w-full rounded-xl glass-pill px-4 py-2.5 text-base outline-none placeholder:text-tg-hint disabled:opacity-50 mb-2"
              />
              <button
                type="button"
                disabled={!aiPrompt.trim() || aiBusy}
                onClick={generateWithAi}
                className="w-full py-2.5 rounded-xl bg-tg-button text-tg-buttonText font-semibold disabled:opacity-40 flex items-center justify-center gap-1.5"
              >
                {aiBusy ? (
                  <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                ) : (
                  <Wand2 size={16} strokeWidth={2.25} />
                )}
                {aiBusy ? "جار الرسم..." : "ارسم"}
              </button>
              {aiError && <p className="text-red-400 text-xs mt-2">{aiError}</p>}
            </>
          ) : (
            <p className="text-xs text-tg-hint">ميزة حصرية لعضوية VIP</p>
          )}
        </div>

        {!isVip && (
          <button
            type="button"
            onClick={() => openExternalLink(SUBSCRIBE_CONTACT_LINK)}
            className="w-full py-2.5 rounded-xl glass-pill text-xs font-medium flex items-center justify-center gap-1.5"
          >
            <Sparkles size={13} strokeWidth={2.5} />
            رقّي لـVIP لفتح الأقلام الحصرية والرسم بالذكاء الاصطناعي
          </button>
        )}

        <button
          type="button"
          onClick={() => applyAndClose(penType, color)}
          className="w-full py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold"
        >
          ابدأ الرسم
        </button>
      </div>
    </div>
  );
}